import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { Typography } from 'components/DesignSystemV2/Typography';
import { colorsV2 } from 'style/colors-v2';
import {
  IconArrowChevronLeftArrowLeft,
  IconArrowChevronLeftArrowRight
} from 'components/Icon/SVGIcons';

const Main = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;

  > svg {
    font-size: 20px;
  }
`;

const ButtonWrapper = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  border: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:not(.navigator) {
    background: ${colorsV2.gray10};
  }

  &:hover:not(:disabled):not(.active) {
    background: ${colorsV2.gray20};
  }

  &.active {
    background: ${colorsV2.primary100};
    > p {
      color: ${colorsV2.white100};
    }
  }
`;

const sharedIconProps = {
  width: 14,
  height: 14,
  color: colorsV2.grey100
};

export const Pagination = ({
  totalPage,
  onSelect,
  activePage,
  visiblePages = 3
}) => {
  const getVisiblePageNumbers = () => {
    const pages = [];
    const halfVisible = Math.floor(visiblePages / 2);

    // Always show first page
    pages.push(0);

    let start = Math.max(1, activePage - halfVisible);
    let end = Math.min(totalPage - 2, activePage + halfVisible);

    // Adjust range if we're near the edges
    if (activePage < halfVisible) {
      end = Math.min(visiblePages - 1, totalPage - 2);
    }
    if (activePage > totalPage - halfVisible - 2) {
      start = Math.max(1, totalPage - visiblePages);
    }

    // Add ellipsis after first page
    if (start > 1) {
      pages.push('...');
    }

    // Add visible page numbers
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page
    if (end < totalPage - 2) {
      pages.push('...');
    }

    // Always show last page if we have more than one page
    if (totalPage > 1) {
      pages.push(totalPage - 1);
    }

    return pages;
  };

  const onNextPage = () => {
    if (activePage === totalPage - 1) return;
    onSelect(activePage + 1);
  };

  const onPrevPage = () => {
    if (activePage === 0) return;
    onSelect(activePage - 1);
  };
  return (
    <Main>
      <ButtonWrapper
        className="navigator"
        onClick={onPrevPage}
        disabled={activePage === 0}
      >
        <IconArrowChevronLeftArrowLeft {...sharedIconProps} />
      </ButtonWrapper>

      {getVisiblePageNumbers().map((pageNum, idx) => {
        if (pageNum === '...') {
          return (
            <Typography key={`ellipsis-${idx}`} variant="medium/14-20">
              ...
            </Typography>
          );
        }

        return (
          <ButtonWrapper
            className={cl('page-number', {
              active: pageNum === activePage
            })}
            key={pageNum}
            onClick={() => onSelect(pageNum)}
          >
            <Typography variant="medium/14-20">{pageNum + 1}</Typography>
          </ButtonWrapper>
        );
      })}

      <ButtonWrapper
        className="navigator"
        onClick={onNextPage}
        disabled={activePage === totalPage - 1}
      >
        <IconArrowChevronLeftArrowRight {...sharedIconProps} />
      </ButtonWrapper>
    </Main>
  );
};
